import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Bell,
  Ticket,
  AlertCircle,
  FileText,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  useTicketsChangedListener,
  notifyTicketsChanged,
} from "@shared-core/domain/ticketEvents";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";
import { Button } from "../../primitives/button";
import { useAuth } from "../../../auth/hooks";
import type * as API from "@shared-core/data/api-types";
import { currentTime } from "@shared-core/domain/current-time";

// Maps a notification's dotted `kind` (e.g. "ticket.activity",
// "issue.assigned", "document.published") to a representative icon by its
// namespace prefix. Binding a component is a shell concern; the mapping
// is purely presentational — the notification is otherwise self-describing
// (render `title`, navigate to `link`), never resolved by source module.
const ICON_BY_KIND_PREFIX: Record<string, LucideIcon> = {
  ticket: Ticket,
  issue: AlertCircle,
  document: FileText,
  forecast: TrendingUp,
};

function iconForKind(kind: string): LucideIcon {
  const prefix = kind.split(".")[0] ?? "";
  return ICON_BY_KIND_PREFIX[prefix] ?? Bell;
}

export function NotificationBell() {
  const dataAdapter = useDataAdapter();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<API.Notification[]>([]);

  const loadNotifications = async () => {
    if (!user) return;
    try {
      const result = await dataAdapter.listNotifications({ limit: 10 });
      setNotifications(result);
    } catch (error) {
      console.error("notifications.bell.load_failed", {
        exc_type: error instanceof Error ? error.name : typeof error,
      });
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAdapter, user?.id]);

  // Reuses the existing ticket-mutation bus rather than introducing a new
  // generic `notificationsChanged` event: today ticket mutations are the
  // only thing that can produce a notification (the generic feed is empty
  // until Phase 2 wires issue/document emitters into it), so this bus is
  // still the right trigger. A properly-scoped generic event is a Phase 2
  // concern, once there is more than one real producer to design it around.
  useTicketsChangedListener(loadNotifications);

  const unread = notifications.filter((n) => !n.is_read);

  const handleMarkAllRead = async () => {
    if (unread.length === 0) return;
    await dataAdapter.markNotificationsRead({ all: true });
    await loadNotifications();
    notifyTicketsChanged();
  };

  const handleSelectNotification = async (notification: API.Notification) => {
    if (!notification.is_read) {
      await dataAdapter.markNotificationsRead({ ids: [notification.id] });
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id
            ? { ...n, is_read: true, read_at: currentTime().toISOString() }
            : n,
        ),
      );
      notifyTicketsChanged();
    }
    navigate(notification.link);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-surface-hover text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Notifications"
        >
          <Bell className="size-5" />
          {unread.length > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-medium bg-red-500 text-white rounded-full">
              {unread.length > 9 ? "9+" : unread.length}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-sm font-medium">Notifications</span>
          {unread.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2"
              onClick={handleMarkAllRead}
            >
              Mark all read
            </Button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="px-2 py-4 text-sm text-muted-foreground text-center">
            No notifications
          </div>
        ) : (
          notifications.map((notification) => {
            const Icon = iconForKind(notification.kind);
            return (
              <DropdownMenuItem
                key={notification.id}
                onClick={() => handleSelectNotification(notification)}
                className="flex items-start gap-2 py-2"
              >
                <span
                  className={`mt-2 size-1.5 rounded-full shrink-0 ${notification.is_read ? "bg-transparent" : "bg-primary"}`}
                />
                <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{notification.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(notification.created_at), {
                      addSuffix: true,
                    })}
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
